import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAssessmentDietsQuery,
  useCreateAssessmentDietMutation,
  useActivateAssessmentDietMutation,
  useCloseAssessmentDietMutation,
  useDeleteAssessmentDietMutation,
  AssessmentDiet,
} from "../api/logbookAssessmentDietApi";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";

function StatusBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    ongoing: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-gray-100 text-gray-600 border-gray-200",
  };
  const cls = map[status || ""] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>
      {status || "—"}
    </span>
  );
}

export default function AdminAssessmentDietsPage() {
  const navigate = useNavigate();
  const { data: diets = [], isLoading, isError, refetch } = useGetAssessmentDietsQuery();

  const [createDiet, { isLoading: creating }] = useCreateAssessmentDietMutation();
  const [activateDiet] = useActivateAssessmentDietMutation();
  const [closeDiet] = useCloseAssessmentDietMutation();
  const [deleteDiet] = useDeleteAssessmentDietMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", start_date: "", end_date: "", notes: "" });
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(null);
    setOk(null);
    if (!form.title.trim()) {
      setErr("Please enter a title.");
      return;
    }
    try {
      await createDiet({
        title: form.title.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        notes: form.notes || null,
      }).unwrap();
      setOk("Assessment diet created.");
      setForm({ title: "", start_date: "", end_date: "", notes: "" });
      setOpen(false);
    } catch (e: any) {
      setErr(String(e?.data?.message || e?.message || "Failed to create assessment diet."));
    }
  };

  const runAction = async (fn: () => Promise<any>, successMsg: string) => {
    setErr(null);
    setOk(null);
    try {
      await fn();
      setOk(successMsg);
    } catch (e: any) {
      setErr(String(e?.data?.message || e?.message || "Action failed."));
    }
  };

  return (
    <div className="bg-white border rounded-xl p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Logbook Assessment</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create an assessment cycle, attach assessors, and assign submitted logbooks to them.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>
          <AccessGate perm={PERMS.logbook_diet.create}>
            <button
              onClick={() => {
                setErr(null);
                setOk(null);
                setOpen(true);
              }}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
            >
              New Assessment Diet
            </button>
          </AccessGate>
        </div>
      </div>

      {err ? (
        <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-100 rounded p-3">{err}</div>
      ) : null}
      {ok ? (
        <div className="mt-4 text-xs text-green-700 bg-green-50 border border-green-100 rounded p-3">{ok}</div>
      ) : null}

      {/* List */}
      <div className="mt-6 overflow-x-auto">
        {isLoading ? (
          <div className="text-gray-600 py-8">Loading assessment diets…</div>
        ) : isError ? (
          <div className="text-red-600 py-8">Failed to load assessment diets.</div>
        ) : !diets.length ? (
          <div className="text-gray-500 py-8">No assessment diets yet. Create one to get started.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Title</th>
                <th className="py-2">Period</th>
                <th className="py-2">Status</th>
                <th className="py-2">Assessors</th>
                <th className="py-2">Logbooks</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {diets.map((d: AssessmentDiet) => (
                <tr key={d.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 font-semibold text-gray-800">
                    {d.title}
                    {/* An old single-logbook sitting: a pass in it stands for
                        all three stages, not one. Flagged in the list because
                        it changes what every result in the diet means, and the
                        only other way to know is to open each one. */}
                    {d.covers_all_stages ? (
                      <span
                        title="One logbook stood for all three stages in this diet — a pass here completes the whole scheme."
                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-black uppercase tracking-wider align-middle"
                      >
                        All 3 stages
                      </span>
                    ) : null}
                  </td>
                  <td className="py-3 text-gray-600">
                    {d.start_date || "—"} → {d.end_date || "—"}
                  </td>
                  <td className="py-3">
                    <StatusBadge status={d.status} />
                  </td>
                  <td className="py-3">{d.assessors_count ?? 0}</td>
                  <td className="py-3">{d.logbooks_count ?? 0}</td>
                  <td className="py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/admin/logbook-assessment/${d.id}`)}
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                      >
                        Manage
                      </button>

                      {d.status !== "closed" ? (
                        <AccessGate perm={PERMS.logbook_diet.start_assessment}>
                          {d.status === "ongoing" ? (
                            <button
                              onClick={() =>
                                runAction(() => closeDiet(d.id).unwrap(), "Assessment diet closed.")
                              }
                              className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                            >
                              Close
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                runAction(() => activateDiet(d.id).unwrap(), "Assessment diet is now ongoing.")
                              }
                              className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                            >
                              Start
                            </button>
                          )}
                        </AccessGate>
                      ) : null}

                      <AccessGate perm={PERMS.logbook_diet.delete}>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete assessment diet "${d.title}"?`)) {
                              runAction(() => deleteDiet(d.id).unwrap(), "Assessment diet deleted.");
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs"
                        >
                          Delete
                        </button>
                      </AccessGate>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white border shadow-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold">New Assessment Diet</h2>
              <button onClick={() => setOpen(false)} className="px-2 py-1 rounded border hover:bg-gray-50">
                ✕
              </button>
            </div>

            {err ? (
              <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded p-3">{err}</div>
            ) : null}

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600">Title</label>
                <input
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. 2026 Q1 Logbook Assessment"
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Start date</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => set("start_date", e.target.value)}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">End date</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => set("end_date", e.target.value)}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Notes (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
                disabled={creating}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={creating}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
              >
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
